import _ from 'lodash';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Redirect,
  Route,
  RouteProps,
  Switch,
  match,
  matchPath,
  useLocation,
} from 'react-router-dom';
import { usePageSearchParams } from '../../../../creativeHub/modules/search/model';
import * as routerActions from '../actions';

export type RouteDecl = {
  key: string;
  page: string;
  props: {
    path: string;
    exact: boolean;
    component: () => JSX.Element | null;
  };
};

const RouteHandler: React.FC<{ routes: RouteDecl[] }> = ({ routes }) => {
  const d = useDispatch();
  const searchParams = usePageSearchParams();

  const pagePathChanged = (page: string, match: any, location: any) => {
    d(
      routerActions.pagePathChanged({
        page,
        match,
        location,
        // Кладём в стор только сериализуемые поля: `usePageSearchParams` возвращает
        // ещё `_url`/`_urlWithFile` (функцию), а функции в redux state недопустимы.
        hubSearchPageParams: searchParams
          ? _.pick(searchParams, ['global', 'query', 'scope', 'tags', 'ai'])
          : undefined,
      }),
    );
  };

  let matchedRoute: RouteDecl | null = null;
  let matchParams: match | null = null;

  // В этом месте нам нужно посмотреть на каждый `route` из `routes` и найти тот, который больше подходит
  // Однако, useRouteMatch внутри себя использует useContext, а это нарушение Rules of Hooks.
  // see: https://github.com/remix-run/react-router/blob/78249eb6ce9a28df4691cc61caa0467fed8b7ac0/packages/react-router/modules/hooks.js#L44
  // здесь я вытащил в общие переменные те части логики, которые используют контекст
  const location = useLocation();
  // const RR_Context = useContext(__RouterContext);

  // и оставил ту часть, которую можно вызывать много раз без нарушения правил
  const _use_route_match = (path: RouteProps) => matchPath(location.pathname, path);

  for (const route of routes) {
    const matchAttempt = _use_route_match({ path: route.props.path, exact: route.props.exact });
    if (matchAttempt) {
      matchedRoute = route;
      matchParams = matchAttempt;
      break;
    }
  }

  const [prevRouteKey, setPrevRouteKey] = useState<string>();
  const [prevMatchParams, setPrevMatchParams] = useState<any>();
  const [prevLocationSearch, setPrevLocationSearch] = useState<string>();
  const [prevSearchParams, setPrevSearchParams] = useState<typeof searchParams>(searchParams);

  const sameSearchParams =
    prevSearchParams === null || searchParams === null
      ? prevSearchParams === searchParams
      : _.isEqual(
          _.pick(searchParams, ['global', 'query', 'scope', 'tags', 'ai']),
          _.pick(prevSearchParams, ['global', 'query', 'scope', 'tags', 'ai']),
        );
  if (matchParams === null) return null;
  if (matchedRoute === null) return null;
  if (
    prevRouteKey === matchedRoute.key &&
    _.isEqual(prevMatchParams, matchParams.params) &&
    sameSearchParams &&
    prevLocationSearch === location.search
  ) {
    return null;
  }

  setPrevRouteKey(matchedRoute.key);
  setPrevMatchParams(matchParams.params);
  setPrevLocationSearch(location.search);
  setPrevSearchParams(searchParams);
  pagePathChanged(matchedRoute.page, matchParams, location);

  return null;
};

export const GeneralRoutes = (props: { routes: RouteDecl[]; defaultUrl: string }) => (
  <>
    <Switch>
      {props.routes.map((route) => (
        <Route key={route.key} {...route.props} />
      ))}
      <Route>
        <Redirect to={props.defaultUrl} />
      </Route>
    </Switch>
    <RouteHandler routes={props.routes} />
  </>
);
